import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Clock, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | success | failed | timeout
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }

    let active = true;
    const poll = async () => {
      if (!active) return;
      attemptsRef.current += 1;
      if (attemptsRef.current > 15) { // 15 × 2s = 30s
        setState('timeout');
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setState('success');
          if (!clearedRef.current) { clearCart(); clearedRef.current = true; }
          return;
        }
        if (['failed', 'expired'].includes(data.payment_status)) {
          setState('failed');
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      {state === 'polling' && (
        <>
          <Loader2 size={64} className="mx-auto text-[#00847A] animate-spin mb-6" />
          <h1 className="text-3xl font-black text-[#262626] mb-2">Vérification du paiement…</h1>
          <p className="text-[#595959]">Merci de patienter, nous confirmons votre paiement.</p>
        </>
      )}
      {state === 'success' && (
        <>
          <CheckCircle2 size={80} className="mx-auto text-[#00847A] mb-6" />
          <h1 className="text-3xl font-black text-[#262626] mb-2">Paiement confirmé !</h1>
          <p className="text-[#595959] mb-6">Merci pour votre commande. Un email de confirmation vous a été envoyé.</p>
          <div className="bg-[#F0F9F8] border border-[#00847A]/20 rounded-xl p-4 text-left text-sm text-[#262626] mb-6">
            <div className="font-semibold mb-1">N° de session</div>
            <div className="text-xs text-[#595959] font-mono break-all">{sessionId}</div>
          </div>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/')} className="bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-11 px-6 font-semibold">
              <ShoppingBag size={18} className="mr-2" /> Continuer mes achats
            </Button>
          </div>
        </>
      )}
      {state === 'failed' && (
        <>
          <XCircle size={80} className="mx-auto text-[#FF4D4D] mb-6" />
          <h1 className="text-3xl font-black text-[#262626] mb-2">Paiement échoué</h1>
          <p className="text-[#595959] mb-6">Votre paiement n'a pas pu être finalisé. Votre panier a été conservé.</p>
          <Button onClick={() => navigate('/panier')} className="bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-11 px-6 font-semibold">
            Retour au panier
          </Button>
        </>
      )}
      {state === 'timeout' && (
        <>
          <Clock size={80} className="mx-auto text-[#F59E0B] mb-6" />
          <h1 className="text-3xl font-black text-[#262626] mb-2">Vérification en cours</h1>
          <p className="text-[#595959] mb-6">La confirmation prend plus de temps que prévu. Vous recevrez un email dès qu'elle sera finalisée.</p>
          <Link to="/" className="text-[#00847A] font-semibold hover:underline">Retour à l'accueil</Link>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
